import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Modal } from "react-bootstrap";
import { IonIcon } from "@ionic/react";
import { trash } from "ionicons/icons";
import Loading from "../helpers/Loading";
import ClientUpdateModal from "../modals/ClientUpdateModal";
import NewWorkModal from "../modals/newWorkModal.js";
import CustomTable from "../helpers/customtable";
import {
  fetchCreatedItemsForWork,
  fetchCreatedTablesForWork,
  fetchObjectsForWork,
  fetchTables
} from "../../data/storeManager";
import clientApi from "../../data/api/clientApi";
import workApi from "../../data/api/workApi";
import { getClientById, getAllWorks } from "../../data/getters";
import { selectWork } from "../../data/store/actions/workStoreFunctions";
import ErrorModal from "../helpers/ErrorModal";
import { useLanguage } from "../helpers/LanguageContext";
import formatPrice from "../helpers/formatPrice";
import { formatDate, getStatusClassName } from "./clientAnalyzerUtil";
import "../../css/ClientAnalyzer.css";

function ClientWorksTable({ works, t, translateStatus, onRowClick, onDelete }) {
  const rows = works.map((work) => ({
    ...work,
    clientPaidAmount: work.clientPaid || work.clientpaid || 0,
    clientPriceAmount: work.clientPrice || 0,
    laborCostAmount: work.label || 0
  }));
  const dateColumn = (key, label) => ({
    key,
    header: t(label),
    default: t("notAvailable"),
    formatValue: formatDate,
    sortValue: (row) => (row[key] ? new Date(row[key]).getTime() : null)
  });
  const config = {
    rowKey: "workId",
    emptyText: t("noWorkGeneric"),
    pageSize: 10,
    showTotalCount: true,
    labels: { filter: t("filter"), search: t("search"), actions: t("actions") },
    columns: [
      {
        key: "workId",
        header: t("id"),
        default: t("notAvailable"),
        width: "8%"
      },
      {
        key: "name",
        header: t("name"),
        default: t("notAvailable"),
        width: "16%"
      },
      {
        key: "clientPaidAmount",
        header: t("paid"),
        formatValue: formatPrice,
        align: "right",
        width: "10%"
      },
      {
        key: "clientPriceAmount",
        header: t("totalPrice"),
        formatValue: formatPrice,
        align: "right",
        width: "10%"
      },
      {
        key: "laborCostAmount",
        header: t("laborCost"),
        formatValue: formatPrice,
        align: "right",
        width: "10%"
      },
      {
        key: "status",
        header: t("status"),
        default: t("notAvailable"),
        render: (status) => (
          <span className={getStatusClassName(status)}>
            {translateStatus(status)}
          </span>
        )
      },
      dateColumn("measureDate", "measureDate"),
      dateColumn("orderDate", "orderDate"),
      dateColumn("finishDate", "finishDate"),
      {
        key: "actions",
        header: t("actions"),
        width: "8%",
        text: "...",
        appearOnHover: false,
        actionlist: [
          {
            key: "delete",
            label: t("delete"),
            icon: "bi-trash",
            onClick: (work) => onDelete(work.workId)
          }
        ]
      }
    ],
    onRowClick: (work) => onRowClick(work.workId)
  };

  return <CustomTable data={rows} config={config} />;
}

function ClientAnalyzer() {
  const { t } = useLanguage();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { clientId } = useParams();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [workToDelete, setWorkToDelete] = useState(null);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);

  const [showNewWork, setShowNewWork] = useState(false);
  const [showClientUpdateModal, setShowClientUpdateModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [render, setRender] = useState(true);
  const [error, setError] = useState("");
  const allWorks = useSelector((state) => state.works || []);
  const clientWorks = allWorks.filter(
    (work) => String(work.client?.clientId) === String(clientId)
  );
  const translateStatus = (status) => {
    const key = status?.toLowerCase?.();
    return t(key) ?? status;
  };

  // Load client data
  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const clientData = await dispatch(getClientById(clientId));
        setSelectedClient(clientData);
      } catch (err) {
        setError(t("errorLoadClient"));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [render, clientId, dispatch]);

  const workList = clientWorks;

  // Rerender function
  const rerender = () => setRender(!render);

  // Navigation to workAnalyzer
  const handleRowClick = async (workId) => {
    setLoading(true);
    try {
      dispatch(selectWork(workId));
      await fetchTables(workId);
      await fetchObjectsForWork(workId);
      await fetchCreatedItemsForWork(workId);
      await fetchCreatedTablesForWork(workId);
      navigate(`/work/${workId}`);
    } catch (error) {
      setError(t("errorSelectWork"));
    } finally {
      setLoading(false);
    }
  };

  const handleNewWorkClick = () => setShowNewWork(true);
  const closeNewWork = () => setShowNewWork(false);
  const requestWorkDelete = (workId) => {
    setWorkToDelete(workId);
    setShowDeleteConfirm(true);
  };

  const handleNewWork = async (newWork) => {
    try {
      await dispatch(workApi.createWorkApi(newWork));
      setShowNewWork(false);
      const freshWorks = await dispatch(getAllWorks());
      const filtered = freshWorks.filter(
        (w) => String(w.client.clientId) === String(clientId)
      );
    } catch (error) {
      setError(t("errorCreateWork"));
    }
  };

  const handleClientUpdateClose = () => setShowClientUpdateModal(false);

  const handleClientUpdate = async (updatedClientData) => {
    try {
      await dispatch(
        clientApi.updateClientApi(selectedClient.clientId, updatedClientData)
      );
      setShowClientUpdateModal(false);
      rerender();
    } catch (error) {
      setError(t("errorUpdateClient"));
    }
  };

  const handleCloseErrorModal = () => setError("");

  return (
    <div className="client-analyzer">
      <div className="client-analyzer-container">
        {loading ? (
          <Loading />
        ) : (
          <>
            {error && (
              <ErrorModal error={error} onClose={handleCloseErrorModal} />
            )}
            {selectedClient ? (
              <>
                <Modal
                  show={showDeleteConfirm}
                  onHide={() => setShowDeleteConfirm(false)}
                  centered
                >
                  <Modal.Header closeButton>
                    <Modal.Title>{t("deleteWorkTitle")}</Modal.Title>{" "}
                  </Modal.Header>
                  <Modal.Body>
                    {t("deleteWorkConfirm")} <br />
                    <strong>{t("deleteWorkIrreversible")}</strong>
                  </Modal.Body>
                  <Modal.Footer>
                    <Button
                      variant="secondary"
                      onClick={() => setShowDeleteConfirm(false)}
                    >
                      {t("cancel")}{" "}
                    </Button>
                    <Button
                      variant="danger"
                      onClick={async () => {
                        try {
                          await dispatch(workApi.deleteWorkApi(workToDelete));
                          setShowDeleteConfirm(false);
                          setWorkToDelete(null);
                          setShowDeleteSuccess(true);
                        } catch (err) {
                          setError(t("errorDeleteWork"));
                        }
                      }}
                    >
                      {t("delete")}{" "}
                    </Button>
                  </Modal.Footer>
                </Modal>
                <Modal
                  show={showDeleteSuccess}
                  onHide={() => setShowDeleteSuccess(false)}
                  centered
                >
                  <Modal.Header closeButton>
                    <Modal.Title>{t("deleteSuccessTitle")}</Modal.Title>
                  </Modal.Header>
                  <Modal.Body>{t("deleteSuccessMessage")}</Modal.Body>
                  <Modal.Footer>
                    <Button
                      variant="primary"
                      onClick={() => setShowDeleteSuccess(false)}
                    >
                      {t("ok")}
                    </Button>
                  </Modal.Footer>
                </Modal>

                <Modal show={showNewWork} onHide={closeNewWork}>
                  <Modal.Header closeButton>
                    <Modal.Title>{t("newWorkTitle")}</Modal.Title>{" "}
                  </Modal.Header>
                  <Modal.Body>
                    <NewWorkModal
                      show={showNewWork}
                      handleClose={closeNewWork}
                      onSubmit={handleNewWork}
                    />
                  </Modal.Body>
                </Modal>
                <Modal
                  show={showClientUpdateModal}
                  onHide={handleClientUpdateClose}
                >
                  <Modal.Header closeButton>
                    <Modal.Title>{t("editClientTitle")}</Modal.Title>{" "}
                  </Modal.Header>
                  <Modal.Body>
                    <ClientUpdateModal
                      handleClose={handleClientUpdateClose}
                      clientId={selectedClient?.clientId}
                      onUpdate={handleClientUpdate}
                    />
                  </Modal.Body>
                </Modal>
                <div className="client-analyzer-desktop">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <p className="fs-1 text-start mb-0">
                      <strong>{selectedClient?.name} </strong>
                      {t("clientWorks")}
                    </p>
                    <div>
                      <Button
                        variant="primary"
                        onClick={() => setShowClientUpdateModal(true)}
                        className="me-3"
                      >
                        {t("editClient")}
                      </Button>
                      <Button variant="primary" onClick={handleNewWorkClick}>
                        {t("newWork")}
                      </Button>
                    </div>
                  </div>
                  <div className="mb-3">
                    <p className="fs-2 fw-bold text-start">{t("details")}</p>
                    <div className="row">
                      <div className="col-md-6">
                        <p className="fs-5 text-start">
                          {t("phone")}: <span>{selectedClient?.telephone}</span>
                        </p>
                      </div>
                      <div className="col-md-6">
                        <p className="fs-5 text-start">
                          {t("address")}: <span>{selectedClient?.address}</span>
                        </p>
                      </div>
                      <div className="col-md-6">
                        <p className="fs-5 text-start">
                          {t("workCount")}: <span>{clientWorks.length}</span>
                        </p>
                      </div>
                      <div className="col-md-6">
                        <p className="fs-5 text-start">
                          {t("activeWorks")}:{" "}
                          <span>
                            {
                              clientWorks.filter((w) => w.status === "Active")
                                .length
                            }
                          </span>
                        </p>
                      </div>
                      <div className="col-md-6">
                        <p className="fs-5 text-start">
                          {t("completedWorks")}:{" "}
                          <span>
                            {
                              clientWorks.filter(
                                (w) => w.status === "Completed"
                              ).length
                            }
                          </span>
                        </p>
                      </div>
                      <div className="col-md-6">
                        <p className="fs-5 text-start">
                          {t("remainingPayment")}:{" "}
                          <span>
                            {formatPrice(selectedClient?.clientSold || 0)} RON
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <p className="fs-2 fw-bold text-start mb-0">
                      {t("previousWorks")}
                    </p>{" "}
                  </div>
                  {workList.length === 0 ? (
                    <div
                      style={{
                        padding: "2rem",
                        textAlign: "center",
                        color: "#6c757d",
                        background: "#fff",
                        borderRadius: "0.5rem",
                        border: "1px dashed #dee2e6"
                      }}
                    >
                      <p style={{ fontSize: "1.1rem", marginBottom: "0.5rem" }}>
                        {t("noWorkForClient")}
                      </p>
                      <p style={{ margin: 0 }}>{t("createWorkHint")}</p>
                    </div>
                  ) : (
                    <div className="client-works-customtable">
                      <ClientWorksTable
                        works={workList}
                        t={t}
                        translateStatus={translateStatus}
                        onRowClick={handleRowClick}
                        onDelete={requestWorkDelete}
                      />
                    </div>
                  )}
                </div>
                <div className="client-analyzer-tablet">
                  <div className="client-tablet-header">
                    <div>
                      <h1>{selectedClient?.name}</h1>
                      <p>{t("clientWorks")}</p>
                    </div>

                    <div className="client-tablet-actions">
                      <Button
                        variant="primary"
                        onClick={() => setShowClientUpdateModal(true)}
                      >
                        {t("editClient")}
                      </Button>
                      <Button variant="primary" onClick={handleNewWorkClick}>
                        {t("newWork")}
                      </Button>
                    </div>
                  </div>

                  <section className="client-info-panel">
                    <div>
                      <span>{t("phone")}</span>
                      <strong>{selectedClient?.telephone || "--"}</strong>
                    </div>
                    <div>
                      <span>{t("address")}</span>
                      <strong>{selectedClient?.address || "--"}</strong>
                    </div>
                    <div>
                      <span>{t("workCount")}</span>
                      <strong>{clientWorks.length}</strong>
                    </div>
                    <div>
                      <span>{t("activeWorks")}</span>
                      <strong>
                        {
                          clientWorks.filter((w) => w.status === "Active")
                            .length
                        }
                      </strong>
                    </div>
                    <div>
                      <span>{t("completedWorks")}</span>
                      <strong>
                        {
                          clientWorks.filter((w) => w.status === "Completed")
                            .length
                        }
                      </strong>
                    </div>
                    <div>
                      <span>{t("remainingPayment")}</span>
                      <strong>
                        {formatPrice(selectedClient?.clientSold || 0)} RON
                      </strong>
                    </div>
                  </section>

                  <section className="client-works-section">
                    <h2>{t("previousWorks")}</h2>

                    {workList.length === 0 ? (
                      <div className="client-empty-state">
                        <p>{t("noWorkForClient")}</p>
                        <span>{t("createWorkHint")}</span>
                      </div>
                    ) : (
                      <div className="client-works-customtable">
                        <ClientWorksTable
                          works={workList}
                          t={t}
                          translateStatus={translateStatus}
                          onRowClick={handleRowClick}
                          onDelete={requestWorkDelete}
                        />
                      </div>
                    )}
                  </section>
                </div>

                <div className="client-analyzer-mobile">
                  <div className="client-mobile-header">
                    <h1>{selectedClient?.name}</h1>
                    <p>{t("clientWorks")}</p>

                    <div className="client-mobile-actions">
                      <Button
                        variant="primary"
                        onClick={() => setShowClientUpdateModal(true)}
                      >
                        {t("editClient")}
                      </Button>
                      <Button variant="primary" onClick={handleNewWorkClick}>
                        {t("newWork")}
                      </Button>
                    </div>
                  </div>

                  <section className="client-mobile-info">
                    <p>
                      <span>{t("phone")}:</span>{" "}
                      {selectedClient?.telephone || "--"}
                    </p>
                    <p>
                      <span>{t("address")}:</span>{" "}
                      {selectedClient?.address || "--"}
                    </p>
                    <p>
                      <span>{t("workCount")}:</span> {clientWorks.length}
                    </p>
                    <p>
                      <span>{t("remainingPayment")}:</span>{" "}
                      {formatPrice(selectedClient?.clientSold || 0)} RON
                    </p>
                  </section>

                  <section className="client-mobile-works">
                    <h2>{t("previousWorks")}</h2>

                    {workList.length === 0 ? (
                      <div className="client-empty-state">
                        <p>{t("noWorkForClient")}</p>
                        <span>{t("createWorkHint")}</span>
                      </div>
                    ) : (
                      workList.map((work) => (
                        <div
                          key={work.workId}
                          className="client-mobile-work-card"
                          onClick={() => handleRowClick(work.workId)}
                        >
                          <div className="client-mobile-work-card-header">
                            <strong>{work.name || "--"}</strong>
                            <span className={getStatusClassName(work.status)}>
                              {translateStatus(work.status)}
                            </span>
                          </div>

                          <p>{work.workId}</p>
                          <p>
                            {t("paid")}:{" "}
                            {formatPrice(
                              work.clientPaid || work.clientpaid || 0
                            )}
                          </p>
                          <p>
                            {t("totalPrice")}:{" "}
                            {formatPrice(work.clientPrice || 0)}
                          </p>
                          <p>
                            {t("measureDate")}: {formatDate(work.measureDate)}
                          </p>

                          <div className="client-mobile-card-actions">
                            <Button
                              className="client-delete-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                requestWorkDelete(work.workId);
                              }}
                            >
                              <IonIcon icon={trash} />
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </section>
                </div>
              </>
            ) : (
              <div className="d-flex justify-content-center mt-3">
                <ErrorModal
                  error={t("errorNoClient")}
                  onClose={handleCloseErrorModal}
                />
                <Button
                  variant="primary"
                  onClick={() => navigate("/UserDashboard")}
                >
                  {t("backToDashboard")}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ClientAnalyzer;
